import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ArcoVue from '@arco-design/web-vue'
// Only the icons this console actually renders. Registering the icon
// plugin instead pulls in all 287 of them, and a default-export plugin
// cannot be tree-shaken, so every build shipped the whole set.
import {
  IconApps,
  IconArrowLeft,
  IconBarChart,
  IconBranch,
  IconBug,
  IconCaretDown,
  IconCaretUp,
  IconCheck,
  IconCheckCircleFill,
  IconCloseCircleFill,
  IconCommand,
  IconDashboard,
  IconDesktop,
  IconDown,
  IconFile,
  IconFilter,
  IconGift,
  IconHistory,
  IconHome,
  IconIdcard,
  IconInfoCircle,
  IconLink,
  IconLocation,
  IconLock,
  IconMenu,
  IconMenuUnfold,
  IconPlus,
  IconPublic,
  IconQuestionCircle,
  IconRefresh,
  IconRotateLeft,
  IconRelation,
  IconSafe,
  IconSearch,
  IconSettings,
  IconSkin,
  IconStop,
  IconStorage,
  IconTag,
  IconTool,
  IconUpload,
  IconUser,
  IconUserGroup,
  IconExport,
  IconFullscreen,
  IconFullscreenExit,
  IconThunderbolt,
  IconClose,
  IconDownload,
  IconLoading,
  IconRight,
  IconZoomIn,
  IconZoomOut,
  IconArrowDown,
  IconExclamationCircleFill,
  IconLaunch,
  IconLeft,
} from '@arco-design/web-vue/es/icon'
import '@arco-design/web-vue/dist/arco.css'
// After arco.css on purpose: it overrides Arco's radius tokens.
import './styles/base.css'
import './styles/dark.css'
import './styles/twotone.css'
import './styles/square.css'
import './styles/nowrap.css'
// Last: the phone layout overrides the rules above it.
import './styles/mobile.css'
import App from './App.vue'
import router from './router'
import { i18n } from './i18n'
import { applyBranding } from './utils/branding'
import { bootFailed, recoverStaleChunks } from './utils/boot'

const app = createApp(App)
app.use(createPinia())
app.use(router)
app.use(i18n)
app.use(ArcoVue)
app.component('IconApps', IconApps)
app.component('IconArrowLeft', IconArrowLeft)
app.component('IconBarChart', IconBarChart)
app.component('IconBranch', IconBranch)
app.component('IconBug', IconBug)
app.component('IconCaretDown', IconCaretDown)
app.component('IconCaretUp', IconCaretUp)
app.component('IconCheck', IconCheck)
app.component('IconCheckCircleFill', IconCheckCircleFill)
app.component('IconCloseCircleFill', IconCloseCircleFill)
app.component('IconCommand', IconCommand)
app.component('IconDashboard', IconDashboard)
app.component('IconDesktop', IconDesktop)
app.component('IconDown', IconDown)
app.component('IconFile', IconFile)
app.component('IconFilter', IconFilter)
app.component('IconGift', IconGift)
app.component('IconHistory', IconHistory)
app.component('IconHome', IconHome)
app.component('IconIdcard', IconIdcard)
app.component('IconInfoCircle', IconInfoCircle)
app.component('IconLink', IconLink)
app.component('IconLocation', IconLocation)
app.component('IconLock', IconLock)
app.component('IconMenu', IconMenu)
app.component('IconMenuUnfold', IconMenuUnfold)
app.component('IconPlus', IconPlus)
app.component('IconPublic', IconPublic)
app.component('IconQuestionCircle', IconQuestionCircle)
app.component('IconRefresh', IconRefresh)
app.component('IconRotateLeft', IconRotateLeft)
app.component('IconRelation', IconRelation)
app.component('IconSafe', IconSafe)
app.component('IconSearch', IconSearch)
app.component('IconSettings', IconSettings)
app.component('IconSkin', IconSkin)
app.component('IconStop', IconStop)
app.component('IconStorage', IconStorage)
app.component('IconTag', IconTag)
app.component('IconTool', IconTool)
app.component('IconUpload', IconUpload)
app.component('IconUser', IconUser)
app.component('IconUserGroup', IconUserGroup)
app.component('IconExport', IconExport)
app.component('IconFullscreen', IconFullscreen)
app.component('IconFullscreenExit', IconFullscreenExit)
app.component('IconThunderbolt', IconThunderbolt)
app.component('IconClose', IconClose)
app.component('IconDownload', IconDownload)
app.component('IconLoading', IconLoading)
app.component('IconRight', IconRight)
app.component('IconZoomIn', IconZoomIn)
app.component('IconZoomOut', IconZoomOut)
app.component('IconArrowDown', IconArrowDown)
app.component('IconExclamationCircleFill', IconExclamationCircleFill)
app.component('IconLaunch', IconLaunch)
app.component('IconLeft', IconLeft)

// Branding is fetched but deliberately not awaited: it is a title and an
// icon, and blocking the whole console on one more network round trip
// would make a slow or unreachable settings endpoint look like an app
// that will not start.
void applyBranding()

// Mount only once the first navigation (and its guards) has resolved.
// Mounting synchronously renders App against a not-yet-resolved route
// whose meta is empty, so App's `layout !== 'blank'` check evaluates true
// and flashes the full admin shell for a frame before the router settles
// on the blank-layout login page.
recoverStaleChunks(router)
router.isReady().then(() => app.mount('#app'), bootFailed)
